import { useEffect, useRef, useState, type ReactNode } from "react";

type Variant = "error" | "info" | "ok" | "warn";

const AUTO_DISMISS_MS: Record<Variant, number> = {
  ok: 3000,
  info: 3000,
  error: 5000,
  warn: 5000,
};

type Props = {
  variant: Variant;
  message: ReactNode | null | undefined;
  onDismiss?: () => void;
};

export function AlertBanner({ variant, message, onDismiss }: Props) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const onDismissRef = useRef(onDismiss);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!message) {
      setVisible(false);
      setExiting(false);
      return;
    }

    setVisible(true);
    setExiting(false);

    if (variant === "error") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    const dismissDelay = AUTO_DISMISS_MS[variant];
    const exitLeadMs = 220;
    const hideTimer = window.setTimeout(() => {
      setExiting(true);
    }, Math.max(0, dismissDelay - exitLeadMs));
    const removeTimer = window.setTimeout(() => {
      setVisible(false);
      setExiting(false);
      onDismissRef.current?.();
    }, dismissDelay);

    return () => {
      window.clearTimeout(hideTimer);
      window.clearTimeout(removeTimer);
    };
  }, [message, variant]);

  function dismissNow() {
    setExiting(true);
    window.setTimeout(() => {
      setVisible(false);
      setExiting(false);
      onDismissRef.current?.();
    }, 220);
  }

  if (!message || !visible) {
    return null;
  }

  return (
    <div
      className={`app-alert app-alert--${variant}${exiting ? " app-alert--exit" : ""}`}
      role={variant === "error" ? "alert" : "status"}
      aria-live={variant === "error" ? "assertive" : "polite"}
      data-testid={`app-alert-${variant}`}
    >
      <p className="app-alert__text">{message}</p>
      <button
        type="button"
        className="app-alert__dismiss"
        aria-label="Zamknij komunikat"
        onClick={dismissNow}
      >
        ×
      </button>
    </div>
  );
}
